import React, { useState, useEffect } from 'react';
import { Building2, Plus, Pencil, Trash2, X, Check, TrendingUp } from 'lucide-react';
import { USER_ROLES } from '../utils/roles';
import WatchlistButton from './WatchlistButton';
import WatchlistNewsFeed from './WatchlistNewsFeed';

export default function CompanyManagement({ user, userRole }) {
  // Check if user is admin
  const isAdmin = userRole === USER_ROLES.ADMIN;
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    ticker: '',
    officialRssUrl: '',
    name: '',
    description: '',
    sector: [],
    industry: [],
    marketCap: '',
    isActive: true
  });

  const API_URL = process.env.REACT_APP_API_URL || '';


  // Standard sectors and industries
  const sectors = [
    'Technology',
    'Healthcare',
    'Financials',
    'Consumer Discretionary',
    'Consumer Staples',
    'Industrials',
    'Energy',
    'Materials',
    'Real Estate',
    'Utilities',
    'Communication Services'
  ];

  const industriesBySector = {
    'Technology': ['Software', 'Hardware', 'Semiconductors', 'IT Services', 'Internet Services'],
    'Healthcare': ['Pharmaceuticals', 'Biotechnology', 'Medical Devices', 'Healthcare Services', 'Healthcare Equipment'],
    'Financials': ['Banks', 'Insurance', 'Investment Banking', 'Asset Management', 'Financial Services'],
    'Consumer Discretionary': ['Retail', 'Automotive', 'Hotels & Leisure', 'Consumer Durables', 'Apparel'],
    'Consumer Staples': ['Food & Beverage', 'Household Products', 'Personal Care', 'Tobacco', 'Food Retail'],
    'Industrials': ['Aerospace & Defense', 'Construction', 'Machinery', 'Transportation', 'Electrical Equipment'],
    'Energy': ['Oil & Gas', 'Oil & Gas Equipment', 'Oil & Gas Exploration', 'Renewable Energy', 'Energy Services'],
    'Materials': ['Chemicals', 'Metals & Mining', 'Paper & Forest Products', 'Construction Materials', 'Containers & Packaging'],
    'Real Estate': ['REITs', 'Real Estate Management', 'Real Estate Development', 'Real Estate Services'],
    'Utilities': ['Electric Utilities', 'Gas Utilities', 'Water Utilities', 'Renewable Utilities', 'Multi-Utilities'],
    'Communication Services': ['Telecommunications', 'Media', 'Entertainment', 'Interactive Media', 'Publishing']
  };

  // Get industries for selected sectors
  const getAvailableIndustries = () => {
    if (!formData.sector || formData.sector.length === 0) {
      return Object.values(industriesBySector).flat();
    }
    const sectorArray = Array.isArray(formData.sector) ? formData.sector : [formData.sector];
    return sectorArray.flatMap(s => industriesBySector[s] || []);
  };


  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/api/companies`);
      const data = await response.json();

      if (data.success) {
        setCompanies(data.companies);
      } else {
        setError(data.error || 'Failed to load companies');
      }
    } catch (error) {
      console.error('Error loading companies:', error);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };


  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      ticker: '',
      officialRssUrl: '',
      name: '',
      description: '',
      sector: [],
      industry: [],
      marketCap: '',
      isActive: true
    });
    setShowAddForm(false);
    setEditingCompany(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if user is admin
    if (!isAdmin) {
      alert('❌ Only administrators can add or edit companies!');
      return;
    }

    try {
      const url = editingCompany
        ? `${API_URL}/api/companies/${editingCompany.ticker}`
        : `${API_URL}/api/companies`;

      const method = editingCompany ? 'PUT' : 'POST';

      // Convert arrays to comma-separated strings for backend
      const submitData = {
        ...formData,
        sector: Array.isArray(formData.sector) ? formData.sector.join(', ') : formData.sector,
        industry: Array.isArray(formData.industry) ? formData.industry.join(', ') : formData.industry
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (data.success) {
        await loadCompanies();
        resetForm();
        alert(editingCompany ? 'Company updated successfully!' : 'Company added successfully!');
      } else {
        alert(data.error || 'Failed to save company');
      }
    } catch (error) {
      console.error('Error saving company:', error);
      alert('Failed to save company');
    }
  };

  const handleEdit = (company) => {
    // Check if user is admin
    if (!isAdmin) {
      alert('❌ Only administrators can edit companies!');
      return;
    }

    // Parse sector and industry - handle both arrays and comma-separated strings
    const parseSectorIndustry = (value) => {
      if (Array.isArray(value)) return value;
      if (typeof value === 'string' && value) {
        return value.split(',').map(v => v.trim()).filter(v => v);
      }
      return [];
    };

    setFormData({
      ticker: company.ticker,
      name: company.name,
      description: company.description || '',
      sector: parseSectorIndustry(company.sector),
      industry: parseSectorIndustry(company.industry),
      marketCap: company.marketCap || '',
      officialRssUrl: company.officialRssUrl || '',
      isActive: company.isActive !== undefined ? company.isActive : true
    });
    setEditingCompany(company);
    setShowAddForm(true);
    
    // Scroll to top to show the edit form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (ticker) => {
    // Check if user is admin
    if (!isAdmin) {
      alert('❌ Only administrators can delete companies!');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${ticker}?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/companies/${ticker}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        await loadCompanies();
        alert('Company deleted successfully!');
      } else {
        alert(data.error || 'Failed to delete company');
      }
    } catch (error) {
      console.error('Error deleting company:', error);
      alert('Failed to delete company');
    }
  };

  // Show all companies sorted by ticker
  const filteredCompanies = companies.sort((a, b) => a.ticker.localeCompare(b.ticker));

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-gray-500">Loading companies...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Building2 className="w-8 h-8" />
              Top 100 Companies
            </h1>
            <p className="text-gray-600 mt-2">
              Leading companies across all sectors
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Company
            </button>
          )}
        </div>


        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {editingCompany ? 'Edit Company' : 'Add New Company'}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ticker Symbol *
                  </label>
                  <input
                    type="text"
                    name="ticker"
                    value={formData.ticker}
                    onChange={handleInputChange}
                    disabled={editingCompany}
                    required
                    placeholder="AAPL"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 uppercase"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Apple Inc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sector (Multiple)
                  </label>
                  <select
                    multiple
                    name="sector"
                    value={formData.sector}
                    onChange={(e) => {
                      const options = Array.from(e.target.selectedOptions, option => option.value);
                      setFormData(prev => ({ ...prev, sector: options }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    style={{ minHeight: '120px' }}
                  >
                    {sectors.map(sector => (
                      <option key={sector} value={sector}>{sector}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple sectors</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Industry (Multiple)
                  </label>
                  <select
                    multiple
                    name="industry"
                    value={formData.industry}
                    onChange={(e) => {
                      const options = Array.from(e.target.selectedOptions, option => option.value);
                      setFormData(prev => ({ ...prev, industry: options }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    style={{ minHeight: '120px' }}
                  >
                    {getAvailableIndustries().map(industry => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple industries</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Market Cap
                  </label>
                  <input
                    type="text"
                    name="marketCap"
                    value={formData.marketCap}
                    onChange={handleInputChange}
                    placeholder="2.8T"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Official RSS Feed URLs (Multiple)
                  </label>
                  <textarea
                    name="officialRssUrl"
                    value={formData.officialRssUrl}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="https://www.company.com/newsroom/rss
https://investor.company.com/rss/news.xml
(Enter multiple URLs, one per line)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Optional: RSS feed for company news (investor relations or newsroom)</p>
                </div>

                <div className="flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Active (track this company)
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Brief description of the company..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Check className="w-5 h-5" />
                  {editingCompany ? 'Update' : 'Add'} Company
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Companies List - Left Side (2/3 width) */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              All Companies ({filteredCompanies.length})
            </h2>
          </div>

          {filteredCompanies.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No companies available yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredCompanies.map((company) => (
                <div
                  key={company.id}
                  className="px-6 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-lg font-bold text-blue-600">
                          {company.ticker}
                        </span>
                        <span className="text-lg font-semibold text-gray-900">
                          {company.name}
                        </span>
                        {company.isActive && (
                          <span className="flex items-center gap-1 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                            <TrendingUp className="w-3 h-3" />
                            Active
                          </span>
                        )}
                      </div>

                      {company.description && (
                        <p className="text-gray-600 text-sm mb-2">
                          {company.description}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        {company.sector && (
                          <span>Sector: {company.sector}</span>
                        )}
                        {company.industry && (
                          <span>Industry: {company.industry}</span>
                        )}
                        {company.marketCap && (
                          <span>Market Cap: {company.marketCap}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2 ml-4">
                      {user && (
                        <WatchlistButton
                          ticker={company.ticker}
                          userId={user.uid}
                          size="md"
                        
                          userRole={userRole}
                        />
                      )}
                      {isAdmin && (
                        <>
                          <button
                            onClick={() => handleEdit(company)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(company.ticker)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
            </div>
          </div>

          {/* Popular Stocks - Right Side (1/3 width) */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-4 sticky top-8">
              <h3 className="font-bold mb-4">실시간 인기 종목</h3>
              <WatchlistNewsFeed user={user} userRole={userRole} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
